import React, { useRef, useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    TextField,
    Typography,
    Button,
    IconButton,
    Paper,
    FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';

const OfferLetterDrawer = ({ open, onClose, onSubmit, applicant }) => {
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const [fileName, setFileName] = useState('');
    const [isFromLocalStorage, setIsFromLocalStorage] = useState(false);
    const [formData, setFormData] = useState({
        application_id: applicant?.application_id || '',
        designation: '',
        ctc: '',
        remarks: '',
        date_of_joining: '',
        offer_letter_file: ''
    });
    const [errors, setErrors] = useState({});

    // ✅ On mount (or when drawer opens), check localStorage for a saved offer letter
    useEffect(() => {
        if (open) {
            const lastOfferLetterId = localStorage.getItem('lastOfferLetterId');
            if (lastOfferLetterId) {
                setFileName(lastOfferLetterId);
                setIsFromLocalStorage(true);
                setFormData(prev => ({
                    ...prev,
                    offer_letter_file: lastOfferLetterId // use the filename/id as reference
                }));
            }
        }
    }, [open]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.designation) newErrors.designation = 'Designation is required';
        if (!formData.ctc) newErrors.ctc = 'CTC is required';
        if (!formData.remarks) newErrors.remarks = 'Remarks are required';
        if (!formData.date_of_joining) newErrors.date_of_joining = 'Joining date is required';
        if (!formData.offer_letter_file) newErrors.offer_letter_file = 'Offer letter is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (field) => (event) => {
        setFormData({
            ...formData,
            [field]: event.target.value
        });
        if (errors[field]) {
            setErrors({ ...errors, [field]: '' });
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFormData({ ...formData, offer_letter_file: file });
            setFileName(file.name);
            setIsFromLocalStorage(false);
            if (errors.offer_letter_file) {
                setErrors({ ...errors, offer_letter_file: '' });
            }
        }
    };

    const handleRemoveFile = () => {
        setFormData({ ...formData, offer_letter_file: null });
        setFileName('');
        setIsFromLocalStorage(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleGenerateOfferLetter = () => {
        navigate('/compose-letter', {
            state: { applicant, applicationId: applicant?.application_id }
        });
    };

    // const handleSubmit = () => {
    //     if (validateForm()) {
    //         const formDataToSend = new FormData();

    //         // ✅ Format date to ISO 8601 format: 2025-04-25T14:30:00Z
    //         const rawDate = formData?.date_of_joining; // e.g. "2025-04-25"
    //         const formattedDate = rawDate ? new Date(rawDate).toISOString() : '';

    //         formDataToSend.append('offer_letter_file', formData?.offer_letter_file);
    //         formDataToSend.append('application_id', formData?.application_id);
    //         formDataToSend.append('designation', formData?.designation);
    //         formDataToSend.append('ctc', formData?.ctc);
    //         formDataToSend.append('remarks', formData?.remarks);
    //         formDataToSend.append('date_of_joining', formattedDate); 

    //         onSubmit(formDataToSend);
    //         localStorage.removeItem('lastOfferLetterId');
    //         setIsFromLocalStorage(false);
    //     }
    // };

const handleSubmit = async () => {
    if (validateForm()) {
        const formDataToSend = new FormData();

        // ✅ If file came from localStorage, fetch the PDF blob and append as a File
        if (isFromLocalStorage) {
            try {
                const response = await fetch(fileName); // fileName = 'offer_letter_635_1777117685800.pdf'
                const blob = await response.blob();
                const file = new File([blob], fileName, { type: 'application/pdf' });
                formDataToSend.append('offer_letter_file', file);
            } catch (error) {
                console.error('Failed to fetch offer letter from localStorage reference:', error);
                return;
            }
        } else {
            // ✅ Manual upload — works as before
            formDataToSend.append('offer_letter_file', formData?.offer_letter_file);
        }

        const rawDate = formData?.date_of_joining;
        const formattedDate = rawDate ? new Date(rawDate).toISOString() : '';

        formDataToSend.append('application_id', formData?.application_id);
        formDataToSend.append('designation', formData?.designation);
        formDataToSend.append('ctc', formData?.ctc);
        formDataToSend.append('remarks', formData?.remarks);
        formDataToSend.append('date_of_joining', formattedDate);

        console.log('formDataToSend', formDataToSend);
        onSubmit(formDataToSend);

        // ✅ Clear localStorage after submit
        localStorage.removeItem('lastOfferLetterId');
        setIsFromLocalStorage(false);
    }
};
    // ✅ Also clear state when drawer closes
    const handleClose = () => {
        setFileName('');
        setIsFromLocalStorage(false);
        setFormData({
            application_id: applicant?.application_id || '',
            designation: '',
            ctc: '',
            remarks: '',
            date_of_joining: '',
            offer_letter_file: ''
        });
        setErrors({});
        onClose();
    };

    const drawerTitle = applicant?.status === "Offer Released" ||
        applicant?.status === "Offer Re-released"
        ? "Re-send Offer Letter"
        : "Send Offer Letter";

    return (
        <Drawer anchor="right" open={open} onClose={handleClose}>
            <Box sx={{ width: 400, p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6">{drawerTitle}</Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <TextField
                    fullWidth
                    label="Applicant"
                    value={applicant?.name || ''}
                    margin="normal"
                    variant="outlined"
                    InputProps={{ readOnly: true }}
                    sx={{ backgroundColor: '#f5f5f5' }}
                />

                <TextField
                    fullWidth
                    label="Designation"
                    value={formData.designation}
                    onChange={handleChange('designation')}
                    margin="normal"
                    variant="outlined"
                    required
                    error={!!errors.designation}
                    helperText={errors.designation}
                />

                <TextField
                    fullWidth
                    label="CTC"
                    value={formData.ctc}
                    onChange={handleChange('ctc')}
                    margin="normal"
                    variant="outlined"
                    required
                    error={!!errors.ctc}
                    helperText={errors.ctc}
                />

                <TextField
                    fullWidth
                    label="Remarks"
                    value={formData.remarks}
                    onChange={handleChange('remarks')}
                    margin="normal"
                    variant="outlined"
                    multiline
                    rows={4}
                    required
                    error={!!errors.remarks}
                    helperText={errors.remarks}
                />

                <TextField
                    fullWidth
                    label="Date of Joining"
                    value={formData.date_of_joining}
                    onChange={handleChange('date_of_joining')}
                    margin="normal"
                    variant="outlined"
                    type="date"
                    required
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.date_of_joining}
                    helperText={errors.date_of_joining}
                />

                <Box sx={{ mt: 3, mb: 2 }}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />

                    {/* ✅ Show attached file (from localStorage OR manual upload) */}
                    {fileName ? (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1,
                                border: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '85%' }}>
                                <DescriptionIcon sx={{ color: '#0044C6', flexShrink: 0 }} />
                                <Typography noWrap sx={{ fontSize: '0.875rem' }}>
                                    {fileName}
                                </Typography>
                                {/* ✅ Badge to indicate it came from localStorage / generated letter */}
                                {isFromLocalStorage && (
                                    <Typography
                                        sx={{
                                            fontSize: '0.65rem',
                                            backgroundColor: '#e8f0fe',
                                            color: '#0044C6',
                                            px: 0.8,
                                            py: 0.2,
                                            borderRadius: '4px',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Generated
                                    </Typography>
                                )}
                            </Box>
                            <IconButton
                                size="small"
                                onClick={handleRemoveFile}
                                sx={{ color: '#FF6B6B' }}
                            >
                                <CancelIcon />
                            </IconButton>
                        </Paper>
                    ) : (
                        // ✅ Only show Attach + Generate buttons when NO file is attached
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<AttachFileIcon />}
                                onClick={() => fileInputRef.current.click()}
                                sx={{
                                    width: '100%',
                                    borderColor: errors.offer_letter_file ? '#d32f2f' : '#0044C6',
                                    color: errors.offer_letter_file ? '#d32f2f' : '#0044C6',
                                    '&:hover': { borderColor: '#336EE8', color: '#336EE8' }
                                }}
                            >
                                Attach Offer Letter
                            </Button>
                            {errors.offer_letter_file && (
                                <FormHelperText error>{errors.offer_letter_file}</FormHelperText>
                            )}

                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<DescriptionIcon />}
                                onClick={handleGenerateOfferLetter}
                                sx={{
                                    mt: 2,
                                    borderColor: '#0044C6',
                                    color: '#0044C6',
                                    '&:hover': { borderColor: '#336EE8', color: '#336EE8' }
                                }}
                            >
                                Generate Offer Letter
                            </Button>
                        </>
                    )}
                </Box>

                <Button
                    fullWidth
                    variant="contained"
                    sx={{
                        backgroundColor: '#0044C6',
                        '&:hover': { backgroundColor: '#336EE8' }
                    }}
                    onClick={handleSubmit}
                >
                    {applicant?.status === "Offer Released" || applicant?.status === "Offer Re-released"
                        ? "Re-send Offer"
                        : "Send Offer"}
                </Button>
            </Box>
        </Drawer>
    );
};

export default OfferLetterDrawer;